<style scoped>
.row-clickable {
	cursor: pointer;
	transition: background-color 0.15s ease;
}
.row-clickable:hover {
	background-color: rgba(13, 110, 253, 0.05);
}
.row-clickable.table-active {
	background-color: rgba(13, 110, 253, 0.1);
}
.progress-bar-unreviewed {
	background-color: #dc3545;
}
.progress-bar-reviewed {
	background-color: #198754;
}
</style>

<template>
	<HorizontalSplit>
		<template #top>
			<div style="position: relative; width: 100%; height: 100%">
				<mgl-map
					:map-style="mapStyleURL(ALIDADE_SMOOTH)"
					:center="mapCenter"
					:zoom="mapZoom"
					@map:moveend="onMoveEnd"
				>
					<mgl-raster-source
						source-id="planet"
						:tiles="[planetTileURL]"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer layer-id="planet" />
					</mgl-raster-source>

					<mgl-geo-json-source
						v-if="itemFootprint"
						source-id="item-bounds"
						:data="itemFootprint"
					>
						<mgl-fill-layer
							layer-id="item-bounds-fill"
							:paint="{
								'fill-color': '#0d6efd',
								'fill-opacity': 0.15,
							}"
						/>
						<mgl-line-layer
							layer-id="item-bounds-line"
							:paint="{
								'line-color': '#0d6efd',
								'line-width': 1.5,
							}"
						/>
					</mgl-geo-json-source>

					<mgl-geo-json-source
						v-if="tileBoundsCollection"
						source-id="tile-bounds"
						:data="tileBoundsCollection"
					>
						<mgl-line-layer
							layer-id="tile-bounds-line"
							:paint="{
								'line-color': '#0d6efd',
								'line-width': 1,
							}"
						/>
					</mgl-geo-json-source>

					<mgl-geo-json-source
						v-if="selectedTileBounds"
						source-id="selected-tile-bounds"
						:data="selectedTileBounds"
					>
						<mgl-line-layer
							layer-id="selected-tile-outline"
							:paint="{
								'line-color': '#ffc107',
								'line-width': 3,
							}"
						/>
					</mgl-geo-json-source>
				</mgl-map>
			</div>
		</template>

		<template #bottom>
			<div class="p-3" v-if="loading" style="height: 100%">
				<div
					class="d-flex align-items-center justify-content-center h-100 text-muted"
				>
					<div class="text-center">
						<div class="spinner-border text-primary mb-2" role="status"></div>
						<p class="small">Loading review sections...</p>
					</div>
				</div>
			</div>

			<div class="p-3" v-else-if="error">
				<div class="alert alert-danger">{{ error }}</div>
			</div>

			<div class="p-3" v-else>
				<div class="d-flex justify-content-between align-items-center mb-2">
					<h6 class="mb-0">
						<i class="bi bi-grid-3x3-gap-fill"></i>
						Item
						<span class="font-monospace">{{ itemId }}</span>
						&mdash; Review Sections ({{ tiles.length }})
					</h6>
					<RouterLink
						to="/_/sudo/vision-tasks/pool-corpus"
						class="btn btn-outline-secondary btn-sm"
					>
						<i class="bi bi-arrow-left"></i> Back
					</RouterLink>
				</div>

				<div class="table-responsive">
					<table class="table table-sm table-hover">
						<thead class="table-light">
							<tr>
								<th></th>
								<th style="width: 130px">Tile</th>
								<th class="text-end" style="width: 80px">Unreviewed</th>
								<th class="text-end" style="width: 80px">Reviewed</th>
								<th class="text-end" style="width: 80px">Total</th>
								<th>Progress</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="tile in tiles"
								:key="`${tile.z}/${tile.x}/${tile.y}`"
								class="row-clickable"
								:class="{
									'table-active':
										selectedTileKey === `${tile.z}/${tile.x}/${tile.y}`,
								}"
								@click="selectTile(tile)"
							>
								<td class="text-nowrap">
									<RouterLink
										:to="`/_/sudo/vision-tasks/pool-corpus/item/${itemId}/${tile.z}/${tile.x}/${tile.y}`"
										class="btn btn-outline-primary btn-sm py-0"
										title="Review this tile"
									>
										<i class="bi bi-play-fill"></i>
									</RouterLink>
								</td>
								<td class="font-monospace small">
									{{ tile.z }}/{{ tile.x }}/{{ tile.y }}
								</td>
								<td class="text-end fw-bold text-danger">
									{{ tile.unreviewed_pools }}
								</td>
								<td class="text-end text-success">
									{{ tile.reviewed_pools }}
								</td>
								<td class="text-end">
									{{ tile.total_pools }}
								</td>
								<td>
									<div class="progress" style="height: 6px">
										<div
											class="progress-bar progress-bar-unreviewed"
											:style="{ width: unreviewedPct(tile) + '%' }"
										></div>
										<div
											class="progress-bar progress-bar-reviewed"
											:style="{ width: reviewedPct(tile) + '%' }"
										></div>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>
	</HorizontalSplit>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink, useRoute } from "vue-router";
import {
	MglMap,
	MglRasterSource,
	MglRasterLayer,
	MglGeoJsonSource,
	MglFillLayer,
	MglLineLayer,
	useMap,
} from "vue-maplibre-gl";
import { mapStyleURL } from "@/map/util";
import { ALIDADE_SMOOTH } from "@/map/constants";
import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import { apiClient } from "@/client";
import type {
	VisionReviewItemSummary,
	VisionReviewTileSummary,
} from "@/type/api";

const route = useRoute();
const mapInstance = useMap();

const itemId = route.params.itemId as string;

const tiles = ref<VisionReviewTileSummary[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const selectedTileKey = ref<string | null>(null);
const selectedTile = ref<VisionReviewTileSummary | null>(null);
const itemFootprint = ref<GeoJSON.Polygon | null>(null);
const mapCenter = ref<[number, number]>([-95, 39]);
const mapZoom = ref(4);

const planetTileURL = "/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1";

function mercXToLng(x: number): number {
	return (x / 20037508.34) * 180;
}

function mercYToLat(y: number): number {
	return (
		(180 / Math.PI) *
		(2 * Math.atan(Math.exp((y / 20037508.34) * Math.PI)) - Math.PI / 2)
	);
}

function tileToLng(tx: number, tz: number): number {
	return (tx / Math.pow(2, tz)) * 360 - 180;
}

function tileToLat(ty: number, tz: number): number {
	const latRad = Math.atan(
		Math.sinh(Math.PI * (1 - (2 * ty) / Math.pow(2, tz))),
	);
	return (latRad * 180) / Math.PI;
}

function tileBoundsGeoJSON(tile: VisionReviewTileSummary): GeoJSON.Polygon {
	const west = tileToLng(tile.x, tile.z);
	const east = tileToLng(tile.x + 1, tile.z);
	const north = tileToLat(tile.y, tile.z);
	const south = tileToLat(tile.y + 1, tile.z);
	return {
		type: "Polygon",
		coordinates: [
			[
				[west, north],
				[east, north],
				[east, south],
				[west, south],
				[west, north],
			],
		],
	};
}

function itemFootprintGeoJSON(item: VisionReviewItemSummary): GeoJSON.Polygon {
	const west = mercXToLng(item.min_x);
	const east = mercXToLng(item.max_x);
	const north = mercYToLat(item.max_y);
	const south = mercYToLat(item.min_y);
	return {
		type: "Polygon",
		coordinates: [
			[
				[west, north],
				[east, north],
				[east, south],
				[west, south],
				[west, north],
			],
		],
	};
}

const tileBoundsCollection = computed(() => {
	if (tiles.value.length === 0) return null;
	return {
		type: "FeatureCollection" as const,
		features: tiles.value.map((t) => ({
			type: "Feature" as const,
			geometry: tileBoundsGeoJSON(t),
			properties: {
				tileKey: `${t.z}/${t.x}/${t.y}`,
				unreviewed: t.unreviewed_pools,
			},
		})),
	};
});

const selectedTileBounds = computed(() => {
	if (!selectedTile.value) return null;
	return tileBoundsGeoJSON(selectedTile.value);
});

function unreviewedPct(tile: VisionReviewTileSummary): number {
	if (tile.total_pools === 0) return 0;
	return Math.round((tile.unreviewed_pools / tile.total_pools) * 100);
}

function reviewedPct(tile: VisionReviewTileSummary): number {
	return 100 - unreviewedPct(tile);
}

function selectTile(tile: VisionReviewTileSummary) {
	selectedTileKey.value = `${tile.z}/${tile.x}/${tile.y}`;
	selectedTile.value = tile;

	const map = mapInstance.map;
	if (!map) return;

	const west = tileToLng(tile.x, tile.z);
	const east = tileToLng(tile.x + 1, tile.z);
	const north = tileToLat(tile.y, tile.z);
	const south = tileToLat(tile.y + 1, tile.z);

	map.fitBounds([west, south, east, north], {
		padding: 40,
		duration: 500,
	});
}

function onMoveEnd() {
	const map = mapInstance.map;
	if (!map) return;
	mapCenter.value = [map.getCenter().lng, map.getCenter().lat];
	mapZoom.value = map.getZoom();
}

async function fetchSections() {
	loading.value = true;
	error.value = null;
	try {
		const [sections, items] = await Promise.all([
			apiClient.JSONGet<VisionReviewTileSummary[]>(
				`/api/vision/review/item/${encodeURIComponent(itemId)}/sections`,
			),
			apiClient.JSONGet<VisionReviewItemSummary[]>("/api/vision/review/items"),
		]);
		tiles.value = sections;
		const match = items.find((item) => item.id === itemId);
		if (match) {
			itemFootprint.value = itemFootprintGeoJSON(match);
		}
	} catch (e: unknown) {
		const err = e as {
			response?: { data?: { message?: string } };
			message?: string;
		};
		error.value =
			err?.response?.data?.message ||
			err?.message ||
			"Failed to load review sections.";
	} finally {
		loading.value = false;
	}
}

onMounted(async () => {
	await fetchSections();
});
</script>
